import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

//groupField es el campo por el cual se agrupan en pestañas
export async function exportToExcel(
  data,
  filename,
  groupField = "Tipo",
  importField = "Importe"
) {
  const workbook = new ExcelJS.Workbook();

  const groupedByType = data.reduce((acc, item) => {
    const type = item[groupField] || "Sin tipo";
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {});

  const yearlySummary = {};

  const parseEuroAmount = (str) => {
    if (typeof str !== "string") return 0;
    let cleaned = str.replace(/\s/g, "").replace("€", "");
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    const value = parseFloat(cleaned);
    return isNaN(value) ? 0 : value;
  };

  for (const [type, items] of Object.entries(groupedByType)) {
    items.forEach((item) => {
      const rawDate = item["Fecha"] || item["Date"];
      let year = "No year";

      if (rawDate instanceof Date) {
        year = rawDate.getFullYear();
      } else if (typeof rawDate === "string") {
        const parsed = parseDateString(rawDate);
        year =
          parsed instanceof Date && !isNaN(parsed)
            ? parsed.getFullYear()
            : "No year";
      }

      if (!yearlySummary[year]) yearlySummary[year] = {};
      if (!yearlySummary[year][type]) yearlySummary[year][type] = 0;
      yearlySummary[year][type] += parseEuroAmount(item[importField]);
    });
  }

  const summarySheet = workbook.addWorksheet("Resumen");

  const summaryHeaders = [groupField, importField];
  let currentRow = summarySheet.addRow(["Resumen por año"]);
  currentRow.font = { bold: true };
  summarySheet.addRow([]);

  for (const [year, types] of Object.entries(yearlySummary).sort()) {
    if (!types || Object.keys(types).length === 0) continue;
    summarySheet.addRow([`Año: ${year}`]).font = { bold: true };
    const headerRow = summarySheet.addRow(summaryHeaders);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "EEEEEE" },
      };
    });

    let yearTotal = 0;
    for (const [type, total] of Object.entries(types)) {
      yearTotal += total;
      summarySheet.addRow([
        type,
        `${total.toLocaleString("es-ES", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} €`,
      ]);
    }

    const totalRow = summarySheet.addRow([
      `TOTAL ${year}`,
      `${yearTotal.toLocaleString("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €`,
    ]);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "left" };
    });

    summarySheet.addRow([]);
  }

  summarySheet.columns.forEach((column) => {
    const lengths = column.values
      .filter((v, i) => i !== 0 && v != null)
      .map((v) => v.toString().length);
    const maxLength = lengths.length ? Math.max(...lengths) : 10;
    column.width = maxLength + 2;
  });

  // hojas por tipo
  for (const [type, items] of Object.entries(groupedByType)) {
    const cleanSheetName = type.replace(/[*?:[\]/\\]/g, "").substring(0, 31);
    const worksheet = workbook.addWorksheet(cleanSheetName);

    const headers = Object.keys(items[0]);
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "000000" } };
      cell.alignment = { horizontal: "center" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "EEEEEE" },
      };
      cell.border = {
        bottom: { style: "thin" },
      };
    });

    items.forEach((row) => {
      worksheet.addRow(
        headers.map((key) => {
          const value = row[key];
          if (value == null) return "";
          if (typeof value === "object") return JSON.stringify(value);
          return value.toString();
        })
      );
    });

    const importeIndex = headers.indexOf(importField);
    if (importeIndex !== -1) {
      const sum = items.reduce((acc, item) => {
        const value = parseEuroAmount(item[importField]);
        return acc + value;
      }, 0);

      const totalRowValues = headers.map((_, i) => {
        if (i === 0) return "TOTAL";
        if (i === importeIndex) return sum;
        return "";
      });

      const totalRow = worksheet.addRow(totalRowValues);
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "left" };
        if (colNumber - 1 === importeIndex && typeof cell.value === "number") {
          cell.value = `${cell.value.toLocaleString("es-ES", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })} €`;
        }
      });
    }

    worksheet.columns.forEach((column) => {
      const lengths = column.values
        .filter((v, i) => i !== 0 && v != null)
        .map((v) => v.toString().length);
      const maxLength = lengths.length ? Math.max(...lengths) : 10;
      column.width = maxLength + 2;
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, filename.endsWith(".xlsx") ? filename : filename + ".xlsx");
}

function parseDateString(str) {
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  const [day, month, year] = parts.map((p) => parseInt(p, 10));
  return new Date(year, month - 1, day);
}
